import { combineReducers } from "redux";
import {
  Action,
  PayloadAction,
  ThunkAction,
  configureStore,
} from "@reduxjs/toolkit";
import { initialUrlParamState, urlParamsReducer } from "./slices/urlParamSlice";
import createSagaMiddleware from "@redux-saga/core";
import { loadDataSaga } from "./sagas/dataSagas";
import { dataReducer, initialDataState } from "./slices/dataSlice";
import { accountReducer, initialAccountState } from "./slices/accountSlice";
import { initialWishMarkState, wishMarkReducer } from "./slices/wishMarkSlice";
import { eventReducer, initialEventState } from "./slices/eventSlice";
import {
  conversationReducer,
  initialConversationState,
} from "./slices/conversationSlice";
import { loadState, saveState } from "./localStorage";
import { debounce } from "debounce";
import { initializationSaga } from "./sagas/initializationSaga";
import { campaignReducer, initialCampaignState } from "./slices/campaignSlice";
import {
  notificationReducer,
  initialNotificationState,
} from "./slices/notificationSlice";
import { USER_LOGGED_OUT, USER_SWITCHED_ACCOUNT } from "./actionTypes";

const appReducer = combineReducers({
  urlParams: urlParamsReducer,
  wishlist: wishMarkReducer,
  events: eventReducer,
  account: accountReducer,
  campaigns: campaignReducer,
  conversations: conversationReducer,
  data: dataReducer,
  notifications: notificationReducer,
});

export type AppState = ReturnType<typeof appReducer>;
export type AppThunk = ThunkAction<void, AppState, unknown, Action<string>>;

const rootReducer = (
  state: AppState | undefined,
  action: PayloadAction<any>
) => {
  // Clear all data in redux store to initial.
  if (action.type === USER_LOGGED_OUT) {
    return appReducer(undefined, action);
  }
  // clear all data in redux store if user switch to an additional account
  if (action.type === USER_SWITCHED_ACCOUNT) {
    // Preserve the current account, data, and url states and reset all other reducers to initial state
    return {
      ...appReducer(undefined, action),
      account: state?.account ?? initialAccountState,
      data: state?.data ?? initialDataState,
      urlParams: state?.urlParams ?? initialUrlParamState,
    };
  }

  return appReducer(state, action);
};

const sagaMiddleware = createSagaMiddleware();
const loadedState = loadState();

const initialAppState: AppState = {
  account: {
    ...initialAccountState,
    loggedIn:
      loadedState && loadedState.savedState
        ? loadedState.savedState.loggedIn
        : 1,
    accountData: {
      ...initialAccountState.accountData,
      uid:
        loadedState && loadedState.savedState ? loadedState.savedState.uid : "",
      fullName:
        loadedState && loadedState.savedState
          ? loadedState.savedState.fullName
          : "",
      phoneNumber:
        loadedState && loadedState.savedState
          ? loadedState.savedState.phoneNumber
          : "",
      email:
        loadedState && loadedState.savedState
          ? loadedState.savedState.email
          : "",
      inCreatorMode:
        loadedState && loadedState.savedState
          ? loadedState.savedState.inCreatorMode
          : false,
      profilePicURL:
        loadedState && loadedState.savedState
          ? loadedState.savedState.profilePicURL
          : "",
      isAdmin:
        loadedState && loadedState.savedState
          ? loadedState.savedState.isAdmin
          : false,
    },
    additionalAccountData:
      loadedState &&
      loadedState.savedState &&
      loadedState.savedState.additionalAccountData
        ? loadedState.savedState.additionalAccountData
        : [],
    originalAccountAdmin:
      loadedState && loadedState.savedState
        ? loadedState.savedState.originalAccountAdmin
        : "",
  },
  wishlist: initialWishMarkState,
  events: initialEventState,
  urlParams: initialUrlParamState,
  campaigns: initialCampaignState,
  conversations: initialConversationState,
  data: initialDataState,
  notifications: initialNotificationState,
};

const store = configureStore({
  reducer: rootReducer,
  preloadedState: initialAppState,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {},
    }).concat(sagaMiddleware),
});

// Helper function to save account state to localStorage
const saveAccountStateToStorage = () => {
  const accountState = store.getState().account;
  saveState({
    savedState: {
      loggedIn: accountState ? accountState.loggedIn : 1,
      uid: accountState ? accountState.accountData.uid : "",
      fullName: accountState ? accountState.accountData.fullName : "",
      phoneNumber: accountState ? accountState.accountData.phoneNumber : "",
      email: accountState ? accountState.accountData.email : "",
      inCreatorMode: accountState
        ? accountState.accountData.inCreatorMode
        : false,
      profilePicURL: accountState ? accountState.accountData.profilePicURL : "",
      isAdmin: accountState ? accountState.accountData.isAdmin : false,
      additionalAccountData: accountState
        ? accountState.additionalAccountData
        : [],
      originalAccountAdmin: accountState
        ? accountState.originalAccountAdmin
        : "",
    },
  });
};

// Debounced save for non-critical changes (performance optimization)
const debouncedSave = debounce(saveAccountStateToStorage, 800);
store.subscribe(debouncedSave);

// Save state before page unload to prevent data loss
window.addEventListener("beforeunload", () => {
  saveAccountStateToStorage();
});

sagaMiddleware.run(initializationSaga);
sagaMiddleware.run(loadDataSaga);

export default store;
